/**
 * Utility functions for Pre-Preview access control
 */

/**
 * Get current user's email from localStorage
 * @returns {string|null} - user email or null if not found
 */
function getCurrentUserEmail() {
  try {
    // Check if we're in browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return null
    }

    // Try to get user data from localStorage
    const userData = localStorage.getItem('userData')
    if (userData) {
      const parsedData = JSON.parse(userData)

      // Check various possible paths for email
      const email = parsedData?.email
        || parsedData?.user?.email
        || parsedData?.userInfo?.email
        || parsedData?.profile?.email

      return email || null
    }

    // Fallback: check if email is stored separately
    const userEmail = localStorage.getItem('userEmail')
    if (userEmail) {
      return userEmail
    }

    return null
  } catch (error) {
    return null
  }
}

/**
 * Check if current user has access to Pre-Preview features
 * @returns {boolean} - true if user has access, false otherwise
 */
export function hasPrePreviewAccess() {
  // Check if Pre-Preview mode is enabled
  const isPrePreviewEnabled = process.env.VUE_APP_MODE_PRE_PREVIEW === 'true'
  if (!isPrePreviewEnabled) {
    return false
  }

  // Get allowed users from environment variable
  const allowedUsersEnv = process.env.VUE_APP_PRE_PREVIEW_ALLOWED_USERS
  if (!allowedUsersEnv || allowedUsersEnv.trim() === '') {
    // If no specific users are defined or empty, deny access to all users
    return false
  }

  // Parse allowed users list
  const allowedUsers = allowedUsersEnv.split(',').map(email => email.trim().toLowerCase()).filter(email => email.length > 0)

  // If after filtering empty strings we have no valid emails, deny access
  if (allowedUsers.length === 0) {
    return false
  }

  // Get current user's email from localStorage
  const currentUserEmail = getCurrentUserEmail()
  if (!currentUserEmail) {
    return false
  }

  const normalizedCurrentEmail = currentUserEmail.toLowerCase()
  const hasAccess = allowedUsers.includes(normalizedCurrentEmail)

  // Check if current user's email is in the allowed list
  return hasAccess
}

/**
 * Get list of allowed users for Pre-Preview (for debugging purposes)
 * @returns {string[]} - array of allowed email addresses
 */
export function getAllowedPrePreviewUsers() {
  const allowedUsersEnv = process.env.VUE_APP_PRE_PREVIEW_ALLOWED_USERS
  if (!allowedUsersEnv) {
    return []
  }
  return allowedUsersEnv.split(',').map(email => email.trim())
}

/**
 * Debug function to check Pre-Preview access status
 * Call this from browser console: window.debugPrePreviewAccess()
 */
export function debugPrePreviewAccess() {
  const result = {
    hasAccess: hasPrePreviewAccess(),
    isPrePreviewEnabled: process.env.VUE_APP_MODE_PRE_PREVIEW === 'true',
    allowedUsersEnv: process.env.VUE_APP_PRE_PREVIEW_ALLOWED_USERS,
    allowedUsers: getAllowedPrePreviewUsers(),
    currentUserEmail: getCurrentUserEmail(),
    localStorage: {
      userData: localStorage.getItem('userData'),
      userEmail: localStorage.getItem('userEmail'),
    },
  }

  // Debug output (only in development)
  if (process.env.NODE_ENV === 'development') {
    /* eslint-disable no-console */
    console.group('🔍 Pre-Preview Access Debug')
    console.log('Final Result:', result.hasAccess ? '✅ ACCESS GRANTED' : '❌ ACCESS DENIED')
    console.log('Environment Settings:', {
      VUE_APP_MODE_PRE_PREVIEW: process.env.VUE_APP_MODE_PRE_PREVIEW,
      VUE_APP_PRE_PREVIEW_ALLOWED_USERS: process.env.VUE_APP_PRE_PREVIEW_ALLOWED_USERS,
    })
    console.log('Current User Email:', result.currentUserEmail)
    console.log('Allowed Users:', result.allowedUsers)
    console.log('localStorage userData:', result.localStorage.userData)
    console.groupEnd()
    /* eslint-enable no-console */
  }

  return result
}

// Make debug function available globally
if (typeof window !== 'undefined') {
  window.debugPrePreviewAccess = debugPrePreviewAccess
}
